import { jsx } from 'slate-hyperscript'
import { last, union, isEqual } from 'lodash'
import * as htmlparser2 from 'htmlparser2'
import tinycolor from 'tinycolor2'

import unescapeHTML from './unescapeHTML'
import { defaultLightModeText, defaultDarkModeText } from '../helpers/colors'
import { allSupportedFonts } from '../helpers/fonts'

const lightRgb = tinycolor(defaultLightModeText).toRgb()
const darkRgb = tinycolor(defaultDarkModeText).toRgb()

const guardIterable = (xs) => {
  return Array.from(xs ?? [])
}

/**
 * @typedef {import('domhandler').ChildNode} ChildNode
 */

/**
 * @typedef {import('../types/plottr').AnySlateNode} AnySlateNode
 * @typedef {import('../index').Slate} PltrSlate
 */

/**
 * @typedef ConvertHTMLStringOptions
 * @property {boolean} [stripFont]
 * @property {boolean} [dwimTextColours] Automatically guess default text colours and strip them out.  Useful for removing styles added by dark mode in other apps.
 */

/**
 * Convert the supplied string containing HTML into a Slate object.
 *
 * @param {String} html
 * @param {ConvertHTMLStringOptions} options
 */
export function convertHTMLString(html, options = {}) {
  const isLikelyAWordDocument = html.match(/urn:schemas-microsoft-com:office:office/)
  const isLikelyALibreOfficeDocument = html.match(/content="LibreOffice/)
  const cleanedString = isLikelyAWordDocument
    ? html.replace(/\r\n */g, ' ').replace(/&nbsp;/g, '')
    : isLikelyALibreOfficeDocument
      ? html
          .replace(/ *\n\t */g, ' ')
          .replace(/>\n/g, '>')
          .replace(/\n</g, '<')
          .replace(/\n */g, ' ')
          .trim()
      : html.replace(/\n/g, '')
  const parsed = htmlparser2.parseDocument(`<div id="body">${cleanedString}</div>`)
  const body = parsed.childNodes[0]
  // @ts-ignore
  const slate = deserialize(options)(null)(body)
  return guardIterable(slate.children)
    .reduce(ensureWrapped, { acc: [], prevChild: null })
    .acc.map(unnestLists)
}

const TYPE_OR_VALUE_ATTRIBUTES = ['type', 'text', 'children']

/**
 * Produce true if the provided key is not one of the content-related
 * fields in our slate schema.
 *
 * @param {String} key
 */
const isNotTypeOrValueAttribute = (key) => {
  return TYPE_OR_VALUE_ATTRIBUTES.every((typeOrValueKey) => {
    return !isEqual(key, typeOrValueKey)
  })
}

/**
 * Produce true if the given slate elements have similar attributes
 * for the spans of text they cover.
 *
 * @param {Slate} thisSlateElement
 * @param {Slate} thatSlateElement
 */
export function nonTypeOrValueAttributesAreSame(thisSlateElement, thatSlateElement) {
  const thisKeys = Object.keys(thisSlateElement).filter(isNotTypeOrValueAttribute)
  const thatKeys = Object.keys(thatSlateElement).filter(isNotTypeOrValueAttribute)
  const allKeys = union(thisKeys, thatKeys)

  return (
    thisKeys.length === allKeys.length &&
    thisKeys.every((key) => {
      return isEqual(thisSlateElement[key], thatSlateElement[key])
    })
  )
}

/**
 * Join consecutive nodes together when their content should be part
 * of the same paragraph.
 *
 * @param {Slate} slateNode
 */
export function compressConsecutiveSlateChildren(slateNode) {
  if (Array.isArray(slateNode.children)) {
    const compressedChildren = slateNode.children.map(compressConsecutiveSlateChildren)
    return {
      ...slateNode,
      children: compressedChildren
        .reduce((childrenAcc, nextChild) => {
          const head = childrenAcc[0]
          if (typeof head === 'undefined') {
            return [nextChild]
          } else if (head.type === 'paragraph') {
            // Consecutive paragraphs never get merged.
            return [nextChild, ...childrenAcc]
          } else if (head.type === nextChild.type) {
            // We're in the same sort of thing.  Do the attributes
            // match between peer chidlren?
            //
            // *AND* if they have ids, are they the same?
            if (nonTypeOrValueAttributesAreSame(head, nextChild)) {
              // The attributes match.  Are we dealing with text fields?
              if (typeof head.text === 'string') {
                // Yes, they're text.  Merge the text content.
                return [
                  {
                    ...head,
                    text: head.text + nextChild.text,
                  },
                  ...childrenAcc.slice(1),
                ]
              } else {
                // Attributes match, merge the children if they're present.
                const headHasChildren = Array.isArray(head.children)
                const nextHasChildren = Array.isArray(nextChild.children)
                const newChildren =
                  headHasChildren && nextHasChildren
                    ? head.chidren.concat(nextChild.children)
                    : headHasChildren
                      ? head.children
                      : nextHasChildren
                        ? nextChild.children
                        : null
                return [
                  {
                    ...head,
                    ...(newChildren ? { chidren: newChildren } : {}),
                  },
                  ...childrenAcc.slice(1),
                ]
              }
            } else {
              // There's a mismatch between some attributes in the two
              // children.  Don't merge.
              return [nextChild, ...childrenAcc]
            }
          } else {
            // Node types don't match.  Don't merge.
            return [nextChild, ...childrenAcc]
          }
        }, [])
        .reverse(),
    }
  } else {
    return slateNode
  }
}

export function convertHTMLNodeList(nodeList) {
  // We wrap the content in a paragraph so that the compression
  // consecutive slate children algorithm has a top-level node to
  // insert peers into.
  return compressConsecutiveSlateChildren({
    type: 'paragraph',
    children: guardIterable(nodeList).map(deserialize({})(null)).flat(1),
  }).children
}

const PARAGRAPH_LIKE_ELEMENTS = [
  'paragraph',
  'block-quote',
  'heading-one',
  'heading-two',
  'bulleted-list',
  'numbered-list',
  'image-link',
  'image-data',
]

/**
 * @param {ChildNode} el
 * @param {String} name
 * @return {String | undefined}
 */
const getAttribute = (el, name) => {
  const lowercaseName = name.toLowerCase()
  return 'attributes' in el && Array.isArray(el.attributes)
    ? guardIterable(el.attributes).find((attribute) => {
        return attribute.name.toLowerCase() === lowercaseName
      })?.value
    : undefined
}

/**
 * @typedef Slate
 * @property {String} [type]
 * @property {Array<Slate>} [children]
 * @property {String} [text]
 * @property {boolean} [bold]
 * @property {boolean} [italicy]
 * @property {number} [fontSize]
 * @property {string} [font]
 */

/**
 * @param {{ acc: Array<Slate>, prevChild: Slate | Null }} accumulator
 * @param {Slate} child
 * @return {{ acc: Array<Slate>, prevChild: Slate | Null }}
 */
export const ensureWrapped = (accumulator, child) => {
  const { acc, prevChild } = accumulator
  if (child.type && PARAGRAPH_LIKE_ELEMENTS.includes(child.type)) {
    return { acc: [...acc, child], prevChild: child }
  } else {
    const lastElement = last(acc)
    // Merge text elements that follow paragraphs into their
    // preceeding paragraphs.
    if (lastElement) {
      // Links are merged into the previous paragraph if the previous
      // paragraph came from root-level text.
      if (child.type === 'link') {
        if (typeof prevChild?.text === 'string') {
          return {
            acc: [
              ...acc.slice(0, -1),
              {
                ...lastElement,
                children: [...(lastElement.children ?? []), child],
              },
            ],
            prevChild: child,
          }
        } else {
          return { acc: [...acc, jsx('element', { type: 'paragraph' }, [child])], prevChild: child }
        }
        // If we're just after a link, and we're adding text, create a
        // paragraph from the link and the text.
      } else if (prevChild?.type === 'link') {
        return {
          acc: [
            ...acc.slice(0, -1),
            {
              ...lastElement,
              children: [...(lastElement.children ?? []), child],
            },
          ],
          prevChild: child,
        }
      } else {
        return {
          acc: [
            ...acc.slice(0, -1),
            {
              ...lastElement,
              children: [...(lastElement.children ?? []), child],
            },
          ],
          prevChild: child,
        }
      }
    } else {
      return { acc: [...acc, jsx('element', { type: 'paragraph' }, [child])], prevChild: child }
    }
  }
}

const LIST_TYPES = ['bulleted-list', 'numbered-list']

const ensureAtLeastOneElement = (children) => {
  if (Array.isArray(children) && children.length === 0) {
    return [{ text: '' }]
  } else {
    return children
  }
}

/**
 * @param {String | any} styleString
 * @return {Record<string, String> | (String | any)}
 */
export const parseStyleAttribute = (styleString) => {
  if (typeof styleString === 'string') {
    return styleString.split(';').reduce((acc, next) => {
      const keyValue = next.split(':').map((x) => {
        return x.trim()
      })

      if (keyValue.length === 2) {
        return {
          ...acc,
          [keyValue[0]]: keyValue[1],
        }
      } else {
        return acc
      }
    }, {})
  } else {
    return styleString
  }
}

/**
 * @type {function(ChildNode): function(ChildNode): boolean}
 */
const onlyWhiteSpaceInSpan = (parent) => (node) => {
  return (
    ('data' in node &&
      typeof node.data === 'string' &&
      'tagName' in parent &&
      (parent?.tagName?.toLowerCase?.() === 'span' || !node.data.match(/^[\s]+$/))) ||
    !('data' in node)
  )
}

const safeParseInt = (x) => {
  try {
    return parseInt(x, 10)
  } catch {
    return false
  }
}

/**
 * @param {String | undefined} x
 * @return {number | false}
 */
const parseSizeToPixels = (x) => {
  if (x && x.match(/^[0-9]+\.?[0-9]*(p[xt])?$/)) {
    return safeParseInt(x)
  } else {
    return false
  }
}

const isParagraph = (node) => {
  return node.type === 'paragraph'
}

const isListContainerNode = (node) => {
  return ['bulleted-list', 'numbered-list'].includes(node?.type)
}

const isListItemNode = (node) => {
  return node?.type === 'list-item'
}

/**
 * @param {Slate} slate
 * @return {Slate}
 */
const unnestLists = (slate) => {
  function iter(node) {
    if (Array.isArray(node.children) && isListContainerNode(node)) {
      const children = node.children.reduce((acc, nextChild) => {
        if (isListItemNode(nextChild)) {
          const containerChildren = nextChild.children.filter(isListContainerNode)
          const itemChildren = nextChild.children.filter((child) => {
            return !isListContainerNode(child)
          })
          return [...acc, { ...nextChild, children: itemChildren }, ...containerChildren]
        } else {
          return [...acc, nextChild]
        }
      }, [])
      return {
        ...node,
        children: children.map(unnestLists),
      }
    } else {
      return {
        ...node,
        ...(Array.isArray(node.children) ? { children: node?.children?.map(unnestLists) } : {}),
      }
    }
  }
  return iter(slate)
}

const notMeta = (el) => {
  return el?.name?.toLowerCase() !== 'meta'
}

const notDocType = (el) => {
  return el?.name?.toLowerCase() !== '!doctype'
}

const notHead = (el) => {
  return el?.name?.toLowerCase() !== 'head'
}

const notStyle = (el) => {
  return el?.name?.toLowerCase() !== 'style'
}

const notScript = (el) => {
  return el?.name?.toLowerCase() !== 'script'
}

/**
 * @typedef ColorStyle
 * @property {String} [color]
 */

/**
 * @param {number} x
 * @return {number}
 */
const square = (x) => {
  return x * x
}

/**
 * @param {number} x1
 * @param {number} y1
 * @param {number} z1
 * @param {number} x2
 * @param {number} y2
 * @param {number} z2
 * @return {number}
 */
const distance = (x1, y1, z1, x2, y2, z2) => {
  return Math.sqrt(square(x1 - x2) + square(y1 - y2) + square(z1 - z2))
}

const MIN_DISTANCE_TO_FILTER_TEXT_COLOUR = 35

/**
 * @param {ConvertHTMLStringOptions} options
 * @param {String | undefined} color
 * @return {ColorStyle}
 */
const handleTextColour = (options, color) => {
  if (typeof color === 'string') {
    const { r, g, b } = tinycolor(color).toRgb()
    const minDistance = Math.min(
      distance(r, lightRgb.r, g, lightRgb.g, b, lightRgb.b),
      distance(r, darkRgb.r, g, darkRgb.g, b, darkRgb.b)
    )
    if (options.dwimTextColours && minDistance < MIN_DISTANCE_TO_FILTER_TEXT_COLOUR) {
      return {}
    } else {
      return { color: tinycolor(color).toRgbString() }
    }
  } else {
    return {}
  }
}

/**
 * @param {String} fontStyle
 * @return {String | Null}
 */
const parseFont = (fontStyle) => {
  return (
    allSupportedFonts.find((supportedFont) => {
      return fontStyle.match(new RegExp(supportedFont, 'i'))
    }) ?? null
  )
}

/**
 * @param {Array<Slate>} children
 * @return {Array<Slate>}
 */
const liftListsFromParagraphs = (children) => {
  if (
    children.some(
      (node) =>
        isParagraph(node) && Array.isArray(node.children) && node.children.some(isListContainerNode)
    )
  ) {
    const lifted = children.flatMap((node) => {
      if (
        isParagraph(node) &&
        Array.isArray(node.children) &&
        node.children.some(isListContainerNode)
      ) {
        return node.children.reduce((acc, next) => {
          if (isListContainerNode(next)) {
            return [next, ...acc]
          } else {
            const latest = acc[0]
            if (latest && latest.type === 'paragraph') {
              return [
                {
                  ...latest,
                  children: [...latest.children, next],
                },
                ...acc.slice(1),
              ]
            } else {
              return [
                {
                  ...node,
                  children: [next],
                },
                ...acc,
              ]
            }
          }
        }, [])
      } else {
        return [node]
      }
    })
    return lifted
  } else {
    return children
  }
}

/**
 * FIXME: Improve type for return value.
 *
 * @param {ConvertHTMLStringOptions} options
 * @return {function(ChildNode | Null): function(ChildNode): Slate}
 */
export const deserialize = (options) => (parent) => (el) => {
  /**
   * @type {function(ChildNode | Null): function(ChildNode): (Slate | Array<Slate> | Null)}
   */
  const deserializeIter = (parent) => (el) => {
    const tagName = 'tagName' in el && el.tagName.toLowerCase?.()

    if (el.nodeType === 3 && !parent) {
      const id = getAttribute(el, 'data-id')
      const idObject = id ? { id } : {}
      return jsx('element', { type: 'paragraph' }, [
        { text: unescapeHTML(el.data ?? ''), ...idObject },
      ])
    } else if (el.nodeType === 3 && parent) {
      const id = getAttribute(el, 'data-id')
      const idObject = id ? { id } : {}
      return { text: unescapeHTML(el.data ?? ''), ...idObject }
    } else if (el.nodeType !== 1 || (tagName && ['footer', 'script', 'head'].includes(tagName))) {
      // Not supposed to get here because we guard against it.  See
      // the children constant a little later.
      return null
    } else if (tagName === 'html') {
      const body = (('childNodes' in el && el.childNodes) || []).find((child) => {
        const childTagName = 'tagName' in child && child?.tagName?.toLowerCase?.()
        return childTagName === 'body'
      })
      if (body) {
        return deserializeIter(null)(body)
      }
    }

    /**
     * @param {Slate} node
     * @return {Array<Slate>}
     */
    const flattenNestedParagraphs = (node) => {
      if (node.type === 'paragraph') {
        return (node.children ?? []).flatMap(flattenNestedParagraphs)
      } else {
        return [node]
      }
    }

    /**
     * @type {Array<Slate>}
     */
    const children = liftListsFromParagraphs(
      ensureAtLeastOneElement(
        guardIterable(('childNodes' in el && el.childNodes) || [])
          .filter(onlyWhiteSpaceInSpan(el))
          .filter(notMeta)
          .filter(notDocType)
          .filter(notHead)
          .filter(notStyle)
          .filter(notScript)
          .flatMap(deserializeIter(el))
          .filter(Boolean)
      )
    )

    const style = parseStyleAttribute(
      guardIterable(('attributes' in el && el.attributes) || []).find((attribute) => {
        return attribute.name === 'style'
      })?.value
    )

    const parsedSize = parseSizeToPixels(style?.['font-size'])
    const extraProps = {
      ...handleTextColour(options, style?.color),
      ...(typeof style?.['font-family'] === 'string' &&
        !options.stripFont &&
        (() => {
          const font = parseFont(style?.['font-family'])
          if (font) {
            return { font }
          } else {
            return {}
          }
        })()),
      ...(typeof style?.['font-size'] === 'string' && typeof parsedSize === 'number'
        ? { fontSize: parsedSize }
        : {}),
      ...(style?.['font-weight'] === 'bold' ? { bold: true } : {}),
      ...(style?.['font-style'] === 'italic' ? { italic: true } : {}),
    }

    /**
     * @param {Slate} node
     * @return {Slate}
     */
    const applyExtraPropsToTextLeaves = (node) => {
      if (!('type' in node) && typeof node.text === 'string') {
        // NOTE: the ordering is important!  The existing attributes
        // that were already applied to this leaf should remain there.
        return {
          ...extraProps,
          ...node,
        }
      } else if (Array.isArray(node.children)) {
        return {
          ...node,
          children: node.children.map(applyExtraPropsToTextLeaves),
        }
      } else {
        return node
      }
    }

    /**
     * @param {'anchor' | 'text' | 'focus' | 'fragment' | 'cursor' | 'editor' | 'element' | 'selection'} type
     * @param {Record<String, String>} properties
     * @param {Array<Slate>} jsxChildren
     * @return {Slate}
     */
    const jsxWithProps = (type, properties, jsxChildren) => {
      // @ts-ignore
      return jsx(
        type,
        properties,
        jsxChildren.map((child) => {
          return applyExtraPropsToTextLeaves(child)
        })
      )
    }

    switch (tagName ?? '') {
      case 'div': {
        // if it's only child is a br
        const elementChildren =
          'childNodes' in el &&
          Array.isArray(el.childNodes) &&
          el.childNodes.length === 1 &&
          'tagName' in el.childNodes[0] &&
          el.childNodes[0]?.tagName?.toLowerCase?.() === 'br'
            ? [{ text: '' }]
            : children
        const id = getAttribute(el, 'data-id')
        /**
         * @type {{ id: String } | {}}
         */
        const idObject = id ? { id } : {}
        return jsxWithProps(
          'element',
          { type: 'paragraph', ...idObject },
          elementChildren.flatMap((child) => {
            if (child.type === 'paragraph' && parent !== null) {
              return child.children ?? []
            } else {
              return child
            }
          })
        )
      }
      // case 'br':
      //   return jsxWithProps('element', { type: 'paragraph' }, [{ text: '' }])
      case 'blockquote': {
        const id = getAttribute(el, 'data-id')
        /**
         * @type {{ id: String } | {}}
         */
        const idObject = id ? { id } : {}
        return jsxWithProps(
          'element',
          { type: 'block-quote', ...idObject },
          children.reduce(ensureWrapped, { acc: [], prevChild: null }).acc
        )
      }
      case 'p': {
        const id = getAttribute(el, 'data-id')
        /**
         * @type {{ id: String } | {}}
         */
        const idObject = id ? { id } : {}
        return jsxWithProps(
          'element',
          { type: 'paragraph', ...idObject },
          children.flatMap(flattenNestedParagraphs)
        )
      }
      case 'h1': {
        const id = getAttribute(el, 'data-id')
        /**
         * @type {{ id: String } | {}}
         */
        const idObject = id ? { id } : {}
        return jsxWithProps('element', { type: 'heading-one', ...idObject }, children)
      }
      case 'h2': {
        const id = getAttribute(el, 'data-id')
        /**
         * @type {{ id: String } | {}}
         */
        const idObject = id ? { id } : {}
        return jsxWithProps('element', { type: 'heading-two', ...idObject }, children)
      }
      case 'h3':
      case 'h4':
      case 'h5':
      case 'h6':
      case 'h7': {
        const id = getAttribute(el, 'data-id')
        /**
         * @type {{ id: String } | {}}
         */
        const idObject = id ? { id } : {}
        return jsxWithProps('element', { type: 'heading-two', ...idObject }, children)
      }
      case 'ul': {
        const id = getAttribute(el, 'data-id')
        /**
         * @type {{ id: String } | {}}
         */
        const idObject = id ? { id } : {}
        return jsxWithProps('element', { type: 'bulleted-list', ...idObject }, children)
      }
      case 'li': {
        if (
          children.length === 1 &&
          typeof children[0].type === 'string' &&
          LIST_TYPES.includes(children[0].type)
        ) {
          return children[0]
        } else {
          const id = getAttribute(el, 'data-id')
          /**
           * @type {{ id: String } | {}}
           */
          const idObject = id ? { id } : {}
          return jsxWithProps('element', { type: 'list-item', ...idObject }, children)
        }
      }
      case 'ol': {
        const id = getAttribute(el, 'data-id')
        /**
         * @type {{ id: String } | {}}
         */
        const idObject = id ? { id } : {}
        return jsxWithProps('element', { type: 'numbered-list', ...idObject }, children)
      }
      case 'em':
      case 'i': {
        return children.map((child) => {
          // NOTE: the ordering is important!  The existing attributes
          // that were already applied to this leaf should remain there.
          return {
            ...extraProps,
            ...child,
            italic: true,
          }
        })
      }
      case 'b':
      case 'strong': {
        return children.map((child) => {
          // NOTE: the ordering is important!  The existing attributes
          // that were already applied to this leaf should remain there.
          return {
            ...extraProps,
            ...child,
            bold: true,
          }
        })
      }
      case 'u': {
        return children.map((child) => {
          // NOTE: the ordering is important!  The existing attributes
          // that were already applied to this leaf should remain there.
          return {
            ...extraProps,
            ...child,
            underline: true,
          }
        })
      }
      case 'span': {
        return children.map((child) => {
          // NOTE: the ordering is important!  The existing attributes
          // that were already applied to this leaf should remain there.
          const id = getAttribute(el, 'data-id')
          /**
           * @type {{ id: String } | {}}
           */
          const idObject = id ? { id } : {}
          return {
            ...extraProps,
            ...child,
            ...idObject,
          }
        })
      }
      case 'del':
      case 'strike':
      case 's': {
        return children.map((child) => {
          // NOTE: the ordering is important!  The existing attributes
          // that were already applied to this leaf should remain there.
          return {
            ...extraProps,
            ...child,
            strike: true,
          }
        })
      }
      case 'img': {
        const storageUrl = getAttribute(el, 'data-storageUrl')
        const id = getAttribute(el, 'data-id')
        /**
         * @type {{ id: String } | {}}
         */
        const idObject = id ? { id } : {}
        if (storageUrl) {
          const childrenNodes = [{ text: '', ...idObject }]
          return jsxWithProps(
            'element',
            { type: 'image-link', ...idObject, storageUrl },
            childrenNodes
          )
        } else if (typeof storageUrl === 'undefined') {
          const childrenNodes = [{ text: '', ...idObject }]
          const data = getAttribute(el, 'src')
          if (!data || data?.match?.(/^https?:\/\//)) {
            return null
          } else {
            return jsxWithProps('element', { type: 'image-data', ...idObject, data }, childrenNodes)
          }
        } else {
          // Replace with blank span if others failed
          return jsxWithProps('text', {}, [])
        }
      }
      case 'a': {
        const id = getAttribute(el, 'data-id')
        /**
         * @type {{ id: String } | {}}
         */
        const idObject = id ? { id } : {}
        const url = getAttribute(el, 'href')
        if (url) {
          return jsxWithProps('element', { type: 'link', ...idObject, url }, children)
        } else {
          return null
        }
      }
      default:
        return children
    }
  }

  const result = deserializeIter(parent)(el)
  if (result === null) {
    return jsx('element', { type: 'paragraph' }, [])
  } else if (Array.isArray(result)) {
    return jsx('element', { type: 'paragraph' }, result)
  } else {
    return result
  }
}

/**
 * Produce true if the supplied Slate document is valid according to
 * Plottr's schema.
 *
 * @param {PltrSlate} doc
 */
export const validRCESchema = (doc) => {
  /**
   * @param {AnySlateNode | null} parent
   * @param {AnySlateNode} node
   * @return {Boolean}
   */
  function isValidSlateNode(parent, node) {
    if ('text' in node) {
      return !!(
        parent &&
        !('text' in parent) &&
        // Text nodes may only be children of the following types.
        ['heading-two', 'heading-one', 'link', 'paragraph', 'list-item'].includes(parent.type)
      )
    } else {
      /**
       * @return {boolean}
       */
      const validParentOfParagraphHeadingOrImage = () => {
        return !parent || (parent && !('text' in parent) && parent.type === 'block-quote')
      }
      /**
       * @return {boolean}
       */
      const everyChildIsValid = () => {
        return node.children.every((child) => {
          return isValidSlateNode(node, child)
        })
      }

      switch (node.type) {
        case 'heading-one':
        case 'heading-two':
        case 'paragraph': {
          return validParentOfParagraphHeadingOrImage() && everyChildIsValid()
        }
        case 'bulleted-list':
        case 'numbered-list': {
          return (
            (!parent ||
              (parent &&
                !('text' in parent) &&
                ['bulleted-list', 'numbered-list', 'block-quote'].includes(parent.type))) &&
            everyChildIsValid()
          )
        }
        case 'image-link':
        case 'image-data': {
          return validParentOfParagraphHeadingOrImage() && node.children.length === 0
        }
        case 'link': {
          return !!(
            parent &&
            !('text' in parent) &&
            ['list-item', 'paragraph'].includes(parent.type) &&
            everyChildIsValid()
          )
        }
        case 'list-item': {
          return !!(
            parent &&
            !('text' in parent) &&
            ['bulleted-list', 'numbered-list'].includes(parent.type) &&
            everyChildIsValid()
          )
        }
        default: {
          return false
        }
      }
    }
  }

  return doc.every((node) => isValidSlateNode(null, node))
}

/**
 * Produce true if the supplied HTML doesn't have any of the common
 * errors that t
 *
 * @param {String} html
 * @return {boolean}
 */
export const validHTMLRCESchema = (html) => {
  const slate = convertHTMLString(html)

  return validRCESchema(slate)
}
